import { FunctionalComponent } from 'preact'
import {
    CaptionEditorIcon,
    CropEditorIcon,
    EditorIcon,
    FilterEditorIcon,
    GradientIconProps,
    StickerEditorIcon,
    WatermarkEditorIcon,
} from '../components/icons'
import CreationTool from './creation-tool'
import CreationToolGradient from './gradient'
import CreationToolOutputType from './output-type'

/**
 * Creation tool source editor sections.
 */
enum CreationToolSourceEditor {
    Edit = 'CT_SOURCE_EDITOR_EDIT',
    Caption = 'CT_SOURCE_EDITOR_CAPTION',
    Crop = 'CT_SOURCE_EDITOR_CROP',
    Filters = 'CT_SOURCE_EDITOR_FILTERS',
    Stickers = 'CT_SOURCE_EDITOR_STICKERS',
    Watermark = 'CT_SOURCE_EDITOR_WATERMARK',
}

namespace CreationToolSourceEditor {
    /**
     * Returns all source editors. (in order)
     */
    export function all(): CreationToolSourceEditor[] {
        return [
            CreationToolSourceEditor.Edit,
            CreationToolSourceEditor.Caption,
            // CreationToolSourceEditor.Filters,
            // CreationToolSourceEditor.Stickers,
            CreationToolSourceEditor.Crop,
            // CreationToolSourceEditor.Watermark,
        ]
    }

    /**
     * Returns the source editor's title.
     */
    export function title(editor: CreationToolSourceEditor): string {
        switch (editor) {
            case CreationToolSourceEditor.Edit:
                return 'Edit'
            case CreationToolSourceEditor.Caption:
                return 'Caption'
            case CreationToolSourceEditor.Crop:
                return 'Crop'
            case CreationToolSourceEditor.Filters:
                return 'Filters'
            case CreationToolSourceEditor.Stickers:
                return 'Stickers'
            case CreationToolSourceEditor.Watermark:
                return 'Watermark'
        }
    }

    /**
     * Returns the source editor's menu item title.
     */
    export function menuTitle(editor: CreationToolSourceEditor): string {
        switch (editor) {
            case CreationToolSourceEditor.Edit:
                return 'Open Editor'
            case CreationToolSourceEditor.Caption:
                return 'Add Caption'
            case CreationToolSourceEditor.Crop:
                return 'Crop'
            case CreationToolSourceEditor.Filters:
                return 'Add Filters'
            case CreationToolSourceEditor.Stickers:
                return 'Add Stickers'
            case CreationToolSourceEditor.Watermark:
                return 'Watermark'
        }
    }

    /**
     * Returns the source editor's gradient.
     */
    export function gradient(editor: CreationToolSourceEditor): CreationToolGradient {
        switch (editor) {
            case CreationToolSourceEditor.Edit:
                return CreationToolGradient.RedYellow
            case CreationToolSourceEditor.Caption:
                return CreationToolGradient.RedPink
            case CreationToolSourceEditor.Crop:
                return CreationToolGradient.IndigoBlue
            case CreationToolSourceEditor.Filters:
                return CreationToolGradient.IndigoPurple
            case CreationToolSourceEditor.Stickers:
                return CreationToolGradient.BlueGreen
            case CreationToolSourceEditor.Watermark:
                return CreationToolGradient.IndigoPurple
        }
    }

    /**
     * Returns the editor's supported output types.
     */
    export function supportedOutputTypes(editor: CreationToolSourceEditor): CreationToolOutputType[] {
        switch (editor) {
            case CreationToolSourceEditor.Filters:
            case CreationToolSourceEditor.Stickers:
                return [CreationToolOutputType.Gif, CreationToolOutputType.Sticker]
            default:
                return CreationToolOutputType.all()
        }
    }

    /**
     * Returns the editor's supported creation tools.
     */
    export function supportedCreationTools(editor: CreationToolSourceEditor): CreationTool[] {
        switch (editor) {
            case CreationToolSourceEditor.Watermark:
                return [CreationTool.Upload]
            default:
                return CreationTool.all()
        }
    }

    /**
     * Returns whether or not the editor requires the preview's playhead time.
     */
    export function requiresPreviewPlayheadTime(editor: CreationToolSourceEditor): boolean {
        switch (editor) {
            case CreationToolSourceEditor.Edit:
                return true
            default:
                return false
        }
    }

    /**
     * Returns the oeditor's icon react component.
     */
    export function icon(editor: CreationToolSourceEditor): FunctionalComponent<GradientIconProps> {
        switch (editor) {
            case CreationToolSourceEditor.Edit:
                return EditorIcon
            case CreationToolSourceEditor.Caption:
                return CaptionEditorIcon
            case CreationToolSourceEditor.Crop:
                return CropEditorIcon
            case CreationToolSourceEditor.Filters:
                return FilterEditorIcon
            case CreationToolSourceEditor.Stickers:
                return StickerEditorIcon
            case CreationToolSourceEditor.Watermark:
                return WatermarkEditorIcon
        }
    }
}

export default CreationToolSourceEditor
